import { buildApiUrl } from './api';

/**
 * Blob-URL cache for auth-gated images.
 *
 * The app is bearer-token-only (no cookies), so a plain `<img src>` can't
 * authenticate against `GET /api/images/:id`. We instead `fetch()` the bytes
 * with the `Authorization` header (as every other API call does), turn the blob
 * into an object URL, and hand that to `<img>`. See the Phase 7 serving
 * decision: this keeps the cookieless model intact and needs no service worker.
 *
 * Because a StoredImage id is immutable (replacing an avatar mints a new id),
 * an object URL for a given `/api/images/:id` never goes stale, so we cache it
 * for the session and dedupe concurrent loads. The underlying `fetch()` is
 * still HTTP-cached by the Phase 6 `immutable` headers, so repeat page loads
 * don't re-hit the server.
 */

/** Soft cap on retained object URLs; oldest is evicted + revoked past this. */
const MAX_CACHE_ENTRIES = 200;

/** Resolved object URLs, keyed by the image path (`/api/images/:id`). */
const resolved = new Map<string, string>();

/** In-flight loads, so N avatars sharing one image issue a single fetch. */
const inflight = new Map<string, Promise<string>>();

function currentToken(): string | null {
  try {
    return localStorage.getItem('accessToken');
  } catch {
    return null;
  }
}

/**
 * Resolve an avatar path (`/api/images/:id`) to a displayable object URL,
 * fetching with the bearer token on first use and serving from cache after.
 * Rejects if the image can't be fetched — callers fall back to initials.
 */
export async function loadAvatarObjectUrl(avatarUrl: string): Promise<string> {
  const cached = resolved.get(avatarUrl);
  if (cached) return cached;

  const pending = inflight.get(avatarUrl);
  if (pending) return pending;

  const load = (async () => {
    const token = currentToken();
    const response = await fetch(buildApiUrl(avatarUrl), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      throw new Error(`Failed to load avatar (${response.status})`);
    }
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);

    // Evict the oldest entry once we exceed the soft cap (Map preserves
    // insertion order). We deliberately DON'T revoke on eviction: a live <img>
    // may still be bound to that URL, and revoking would blank it. Dropping the
    // map entry is enough — the browser GCs the blob once no element references
    // it, and a later re-mount just re-fetches (HTTP-cached under the immutable
    // headers). Only the explicit reset revokes (test teardown).
    if (resolved.size >= MAX_CACHE_ENTRIES) {
      const oldestKey = resolved.keys().next().value;
      if (oldestKey !== undefined) resolved.delete(oldestKey);
    }

    resolved.set(avatarUrl, objectUrl);
    return objectUrl;
  })();

  inflight.set(avatarUrl, load);
  try {
    return await load;
  } finally {
    inflight.delete(avatarUrl);
  }
}

/** Test-only: drop all cached object URLs (revoking them) and in-flight loads. */
export function __resetAvatarCache(): void {
  for (const url of resolved.values()) URL.revokeObjectURL(url);
  resolved.clear();
  inflight.clear();
}
